import React, { useMemo } from "react";
import classNames from './style.module.scss';

import CustomSection from "../custom-section";
import PDFTablePage from "../../../themed/pdf/pdfTablePage";
import useProjectDetails from "../../../../hooks/useProjectDetails";

import { Column } from "../../../../utils/models";
import { getAnomalyClassStyle, getAnomalyTypeClassFromAnomaly, getIsAnomalyType, getLNGAnomalyClassStyle } from "../../../../utils/utilities";
import { ANOMALY_TYPE } from "../../../../utils/constants";

interface ClassItem {
    classification: string[];
    description: string;
}

interface DamageClassItem extends ClassItem {
    damageClass: number;
}

interface AnomalyClassItem extends ClassItem {
    anomaly: string;
}

export const CustomAnomalyClass: React.FC = () => {
    const { columns, data } = useMemo(() => {
        const columns: Column[] = [
            {
                path: 'anomaly',
                label: 'Anomaly',
                style: (item) => getLNGAnomalyClassStyle(item.anomaly),
                headerStyle: { backgroundColor: '#ffff05' },
            },
            {
                path: 'class',
                label: 'Condition Class',
                headerStyle: { backgroundColor: '#ffff05' },
                style: (item) => getLNGAnomalyClassStyle(item.anomaly),
                content: (item: AnomalyClassItem) => <span>{getAnomalyTypeClassFromAnomaly(item.anomaly)}</span>
            },
            {
                path: 'classification',
                label: 'Classification',
                headerStyle: { backgroundColor: '#ffff05', textTransform: 'uppercase', textAlign: 'center' },
                style: (item) => getLNGAnomalyClassStyle(item.anomaly),
                content: (item: AnomalyClassItem) => (
                    <ul className={classNames.classification}>
                        {item.classification.map((classification) => <li key={classification} className={classNames.classificationItem}>{classification}</li>)}
                    </ul>
                ),
            },
            {
                path: 'description',
                label: 'General Description',
                style: (item) => getLNGAnomalyClassStyle(item.anomaly),
                headerStyle: { backgroundColor: '#ffff05', textTransform: 'uppercase', textAlign: 'center' },
            },
        ];
        
        const data: AnomalyClassItem[] = [
            {
                anomaly: ANOMALY_TYPE.NONE.ID,
                description: 'No Deterioration with Adequate Protective Measures in Place',
                classification: [
                    'System generally intact with no signs of corrosion',
                    'Connection bolts / nuts in good condition with no corrosion',
                    'Previous repairs in good condition and well protected',
                    'No evidence of misalignment or distortion of members',
                    'Fireproofing in good condition with no cracks or rust stains',
                ]
            },
            {
                anomaly: ANOMALY_TYPE.MINOR.ID,
                description: 'Active Deterioration',
                classification: [
                    'Coating system failure over more than 10% of area',
                    'Heavy rust scale',
                    'Deep pits',
                    'Less than 10% corrosion loss of cross section',
                    'Connection bolts / nuts and anchor bolts / nuts corroded but still defined threads and flats',
                    'Impact damage resulting in member being bent or distorted up to half the depth of the member',
                    'No evidence of other misalignment or distortion of members',
                    'Fireproofing cracks <0.25” (<6mm) wide',
                ]
            },
            {
                anomaly: ANOMALY_TYPE.MINOR_TO_MODERATE.ID,
                description: 'Significant Deterioration',
                classification: [
                    'Corrosion loss of cross section between 10% and 25%',
                    'Holing of member',
                    'Extensive cracking of fireproofing with cracks >0.25” (>6mm) wide',
                    'Impact damage resulting in member being distorted or bent greater than half the depth of the member',
                    'Noticeable deflections or misalignment of members',
                    'Part / Item  leaning from vertical',
                    'Connection deficient, such as one missing bolt or nut',
                    'Severely corroded connection bolts',
                ],
            },
            {
                anomaly: ANOMALY_TYPE.MODERATE.ID,
                description: 'Advanced Deterioration',
                classification: [
                    'Similar to Condition class B, but gives cause for concern',
                    'Corrosion loss of cross section between 35% and 50%',
                    'Two or more of the bolts or nuts broken or missing in a connection',
                    'Gross distortion such as bent out of line greater than 1 times the depth of member',
                    'Gross deflections or misalignment of members',
                    'Member missing',
                    'The member is judged to be potentially dangerous requiring early action',
                ]
            },
            {
                anomaly: ANOMALY_TYPE.MAJOR.ID,
                description: 'Extreme Deterioration / Leak / Not fit for Service',
                classification: [
                    'Similar to Condition class C, but gives cause for serious concern',
                    'Corrosion loss of cross section greater than 50% of the gross section',
                    'Two or more of the bolts or nuts broken or missing in a connection',
                    'Gross distortion such as bent out of line greater than 2 times the depth of member',
                    'Member missing',
                    'The member is judged to be potentially dangerous requiring corrective action',
                    'Functional Failure',
                ],
            },
        ];

        return { columns, data };
    }, []);

    return ( 
       <CustomSection sectionTitle="Anomaly Description">
            <div className="p-1">
                <div className={classNames.header}>
                    Anomaly guide
                </div>

                <div className="mt-1">
                    <PDFTablePage data={data} columns={columns} />
                </div>
            </div>
       </CustomSection>
     );
};

export const CustomDamageClass: React.FC = () => {
    const { columns, data } = useMemo(() => {
        const columns: Column[] = [
            {
                path: 'damageClass',
                label: 'Damage Class (DC)',
                headerStyle: { backgroundColor: '#ffff05' },
                style: (item) => getAnomalyClassStyle({ class: item.damageClass }),
                content: (item: DamageClassItem) => <span>Damage Class {item.damageClass}</span>,
            },
            {
                path: 'classification',
                label: 'Classification',
                headerStyle: { backgroundColor: '#ffff05', textTransform: 'uppercase', textAlign: 'center' },
                style: (item) => getAnomalyClassStyle({ class: item.damageClass }),
                content: (item: DamageClassItem) => (
                    <ul className={classNames.classification}>
                        {item.classification.map((classification) => <li key={classification} className={classNames.classificationItem}>{classification}</li>)}
                    </ul>
                ),
            },
            {
                path: 'description',
                label: 'General Description',
                style: (item) => getAnomalyClassStyle({ class: item.damageClass }),
                headerStyle: { backgroundColor: '#ffff05', textTransform: 'uppercase', textAlign: 'center' },
            },
        ];
        
        const data: DamageClassItem[] = [
            {
                damageClass: 0,
                description: 'No Deterioration with Adequate Protective Measures in Place',
                classification: [
                    'System generally intact with no signs of corrosion',
                    'Connection bolts / nuts in good condition with no corrosion',
                    'Previous repairs in good condition and well protected',
                    'No evidence of misalignment or distortion of members',
                    'Fireproofing in good condition with no cracks or rust stains',
                ]
            },
            {
                damageClass: 1,
                description: 'Active Deterioration',
                classification: [
                    'Coating system failure over more than 10% of area',
                    'Heavy rust scale',
                    'Deep pits',
                    'Less than 10% corrosion loss of cross section',
                    'Connection bolts / nuts and anchor bolts / nuts corroded but still defined threads and flats',
                    'Impact damage resulting in member being bent or distorted up to half the depth of the member',
                    'No evidence of other misalignment or distortion of members',
                    'Fireproofing cracks <0.25” (<6mm) wide',
                ]
            },
            {
                damageClass: 2,
                description: 'Significant Deterioration',
                classification: [
                    'Corrosion loss of cross section between 10% and 25%',
                    'Holing of member',
                    'Extensive cracking of fireproofing with cracks >0.25” (>6mm) wide',
                    'Impact damage resulting in member being distorted or bent greater than half the depth of the member',
                    'Noticeable deflections or misalignment of members',
                    'Part / Item  leaning from vertical',
                    'Connection deficient, such as one missing bolt or nut',
                    'Severely corroded connection bolts',
                ]
            },
            {
                damageClass: 3,
                description: 'Advanced Deterioration',
                classification: [
                    'Corrosion loss of cross section between 35% and 50%',
                    'Two or more of the bolts or nuts broken or missing in a connection',
                    'Gross distortion such as bent out of line greater than 1 times the depth of member',
                    'Gross deflections or misalignment of members',
                    'Member missing',
                    'The member is judged to be potentially dangerous requiring early action',
                ]
            },
            {
                damageClass: 4,
                description: 'Advanced Deterioration',
                classification: [
                    'Similar to Damage Class 3, but gives cause for concern',
                    'Corrosion loss of cross section between 35% and 50%',
                    'Two or more of the bolts or nuts broken or missing in a connection',
                    'Gross distortion such as bent out of line greater than 1 times the depth of member',
                    'Gross deflections or misalignment of members',
                    'Member missing',
                    'The member is judged to be potentially dangerous requiring early action"',
                ]
            },
            {
                damageClass: 5,
                description: 'Extreme Deterioration',
                classification: [
                    'Similar to Damage Class 4, but gives cause for serious concern',
                    'Corrosion loss of cross section greater than 50% of the gross section',
                    'Two or more of the bolts or nuts broken or missing in a connection',
                    'Gross distortion such as bent out of line greater than 2 times the depth of member',
                    'Member missing',
                    'The member is judged to be potentially dangerous requiring corrective action"',
                ]
            },
            {
                damageClass: 6,
                description: 'Leak / Not fit for Service',
                classification: [
                    'Functional Failure'
                ]
            },
        ];

        return { columns, data };
    }, []);

    return ( 
       <CustomSection sectionTitle="Anomaly Damage Class Description">
            <div className="p-1">
                <div className={classNames.header}>
                    Anomaly guide
                </div>

                <div className="mt-1">
                    <PDFTablePage data={data} columns={columns} />
                </div>
            </div>
       </CustomSection>
     );
};

export const CustomClass: React.FC = () => {
    const { projectDetails } = useProjectDetails();
    
    const isAnomalyType = useMemo(() => {
        return projectDetails && getIsAnomalyType(projectDetails);
    }, [projectDetails]);

    if (isAnomalyType) {
        return <CustomAnomalyClass />;
    }

    return <CustomDamageClass />
};

export default CustomClass;